import React, { useCallback, useEffect, useMemo } from 'react';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import DialogMediaPlayer from '../DialogMediaPlayer/DialogMediaPlayer';
import { ReactPlayerProps } from 'react-player';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import { PLayerTriggeredBy } from '../../commands/practitioner/enums/PLayerTriggeredBy';
import { PractitionerMediaPreviewPlayerStyle } from './style';
import { Box } from '@mui/material';
import BackendService from '../../services/BackendService';
import { PlaybackType } from '../../commands/practitioner/enums/PlaybackType';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { classNames } from '../../utils/classNames';

interface PractitionerMediaPreviewPlayerProps {
  className?: string;
}

const PractitionerMediaPreviewPlayer = (props: PractitionerMediaPreviewPlayerProps) => {
  const { className } = props;
  const {
    practitionerMediaURI,
    isPractitionerPlayerVisible,
    isPractitionerReactPlayerStarted,
    setIsPractitionerReactPlayerStarted,
    practitionerMediaType,
    isMutedYoutube,
    setPractitionerVideoTime,
  } = usePractitionerControlsContext();

  const { PractitionerMediaPreviewPlayerPsylarisLibrary } = PractitionerMediaPreviewPlayerStyle;
  const { sessionId, treatmentId } = useAppContext();
  const onPlay = useCallback(async () => {
    setIsPractitionerReactPlayerStarted(true);
  }, [setIsPractitionerReactPlayerStarted]);

  useEffect(() => {
    setPractitionerVideoTime(0);
  }, [practitionerMediaURI, setPractitionerVideoTime]);

  const onPause = useCallback(() => {
    setIsPractitionerReactPlayerStarted(false);
  }, [setIsPractitionerReactPlayerStarted]);

  const onProgress = useCallback(
    (state: { playedSeconds: number }) => {
      setPractitionerVideoTime(Math.round(state.playedSeconds * 1000)); // ms
    },
    [setPractitionerVideoTime]
  );

  const reactPlayerPractitioner: ReactPlayerProps = useMemo(
    () => ({
      url: practitionerMediaURI,
      playing: isPractitionerReactPlayerStarted,
      onPlay,
      onPause,
      onProgress,
      controls: false,
      muted:
        practitionerMediaType === MediaTypes.VIDEO || practitionerMediaType === MediaTypes.AUDIO
          ? isMutedYoutube
          : false,
    }),
    [
      practitionerMediaURI,
      isPractitionerReactPlayerStarted,
      onPlay,
      onPause,
      onProgress,
      practitionerMediaType,
      isMutedYoutube,
    ]
  );
  const videoIsEndedHandler = async () => {
    setPractitionerVideoTime(0);
    try {
      await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, PlaybackType.Skip, 0);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={PractitionerMediaPreviewPlayerPsylarisLibrary} className={classNames('', {}, [className])}>
      <Box
        className={practitionerMediaType !== MediaTypes.IMAGE ? 'player-container' : 'player-container-image'}
        style={{
          display: isPractitionerPlayerVisible && practitionerMediaType !== MediaTypes.AUDIO ? '' : 'none',
        }}
      >
        <Box className={practitionerMediaType !== MediaTypes.IMAGE ? 'player-overlay' : 'player-overlay-image'}>
          <Box
            className="interaction-blocker"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
            }}
          />
          <DialogMediaPlayer
            ReactPlayerProps={reactPlayerPractitioner}
            ReactPlayerTriggeredBy={PLayerTriggeredBy.Practitioner}
            handleMediaEnd={videoIsEndedHandler}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PractitionerMediaPreviewPlayer;
